import { ColumnMeta, FilterResultMethod } from '@api';
import { WithNAFallback } from '@design';
import { getPriceStatus, PriceStatus, Status } from '@helpers';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { priceChangeColors } from '@terminal/utils/common/utils';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import React, { ReactNode } from 'react';

import {
  ColumnMetaWithKey,
  mapValueTypeToFormatter,
} from '../results/screener-table-utils';
import styles from './ScreenerCell.module.scss';

interface ScreenerCellProps {
  value: FilterResultMethod;
  columnMeta: ColumnMetaWithKey | ColumnMeta;
  isDisabled?: boolean;
  customTooltip?: ReactNode;
}

export const ScreenerCell = ({
  value,
  columnMeta,
  isDisabled,
  customTooltip,
}: ScreenerCellProps) => {
  const unit = value.units ?? '';
  const formatter = mapValueTypeToFormatter[columnMeta.value_type || 'integer'];
  const status = columnMeta.directional
    ? getPriceStatus(value.value)
    : PriceStatus.Default;
  const tooltipValue = customTooltip ?? value.tooltip;

  const isRealizedVolatility = columnMeta.name?.includes('Realized Volatility');
  const isPercentage = columnMeta.name?.includes('%') && !isRealizedVolatility;

  const color =
    priceChangeColors[isDisabled ? Status.Disabled : status] ?? undefined;

  return (
    <TooltipContainer
      className={styles.styledTooltip}
      disabled={!tooltipValue}
      inPortal
      label={tooltipValue}
      placement="top-end"
    >
      <span aria-haspopup={!!tooltipValue}>
        <WithNAFallback
          value={
            value.value !== undefined && (
              <div style={{ color }}>
                {isPercentage
                  ? formatPercentageNumber({
                      value: value.value,
                      includePercentSign: !isPercentage,
                      addPlus: true,
                    })
                  : formatter(value.value)}
                {!isPercentage && unit && (
                  <span className={styles.unit}>{unit}</span>
                )}
              </div>
            )
          }
        />
      </span>
    </TooltipContainer>
  );
};
